// ============================================================
// src/components/AddTripModal.jsx
//
// Simplified trip modal. A "trip" is just:
//   - Where are you staying?   (Nominatim address autocomplete)
//   - Trip Start Date          (arrive_dest_at)
//   - Trip End Date            (depart_dest_at / heading home)
//
// These two dates are all the calendar needs to shade stay days
// and mark the return day. All transport / timezone / terminal
// fields have been removed.
//
// ── STYLING ──────────────────────────────────────────────────
// The <style> block that lived in the original JSX has been
// removed. All .atm-* classes are now defined in index.css
// (Section 29 — AddTripModal). This file has zero inline styles.
// All colors reference CSS variables; no hardcoded hex values.
//
// ── DATE INPUTS ──────────────────────────────────────────────
// Dates now use the shared DateInput component from FormInputs.jsx
// for consistent styling across the app. The component applies
// -webkit-appearance: none so iOS Safari respects DPP border/radius.
//
// ── BUTTON CLASSES ───────────────────────────────────────────
// Buttons use both the .btn base class and their variant class
// (.btn-primary, .btn-secondary) so padding, border-radius, and
// font-weight from index.css are always applied.
// ============================================================

import { useState, useEffect, useRef } from 'react'
import { DateInput } from './FormInputs'

const NOMINATIM_BASE       = 'https://nominatim.openstreetmap.org/search'
const NOMINATIM_USER_AGENT = 'DragPocketPlanner/1.0 (https://drag-helper.netlify.app)'


// ── NOMINATIM GEOCODE SEARCH ──────────────────────────────────
// Returns up to 5 address suggestions for a text query.
// Returns [] on network failure so the UI degrades gracefully.
async function nominatimSearch(query) {
  if (!query || query.length < 3) return []
  const params = new URLSearchParams({
    q:             query,
    format:        'json',
    limit:         '5',
    addressdetails: '1',
  })
  try {
    const res = await fetch(`${NOMINATIM_BASE}?${params}`, {
      headers: {
        'Accept-Language': 'en',
        'User-Agent':       NOMINATIM_USER_AGENT,
      },
    })
    return res.ok ? res.json() : []
  } catch {
    return []
  }
}


// ── ADDRESS AUTOCOMPLETE FIELD ────────────────────────────────
// Maintains its own `inputVal` state so keystrokes render
// immediately without waiting for a parent re-render, avoiding
// the one-character-at-a-time lag that occurs when the parent
// owns the input value and re-renders asynchronously.
//
// Props:
//   value      (string)    current address string from parent form state
//   onChange   (function)  called on every keystroke with the new string
//   onSelect   (function)  called with { address, lat, lng, label } when
//                          the user picks a Nominatim suggestion
//   confirmed  (boolean)   true when lat/lng are confirmed; shows green tick
function AddressField({ value, onChange, onSelect, confirmed }) {
  const [inputVal,    setInputVal]    = useState(value || '')
  const [suggestions, setSuggestions] = useState([])
  const [showSug,     setShowSug]     = useState(false)
  const [searching,   setSearching]   = useState(false)
  const timerRef = useRef(null)

  // Sync local input value only when the parent resets the field
  // (modal open / onSelect callback), not on every parent re-render.
  useEffect(() => { setInputVal(value || '') }, [value])

  const handleInput = (e) => {
    const v = e.target.value
    // Update local state immediately so the keystroke appears right away
    setInputVal(v)
    onChange(v)
    clearTimeout(timerRef.current)
    if (v.length < 3) { setSuggestions([]); setShowSug(false); return }
    setSearching(true)
    timerRef.current = setTimeout(async () => {
      const results = await nominatimSearch(v)
      setSuggestions(results)
      setShowSug(results.length > 0)
      setSearching(false)
    }, 400) // 400ms debounce — responsive but not hammering Nominatim
  }

  const pick = (result) => {
    const addr = result.display_name
    setInputVal(addr)
    onChange(addr)
    setSuggestions([])
    setShowSug(false)
    // Bubble up the confirmed coordinates and a short label
    onSelect({
      address: addr,
      lat:     parseFloat(result.lat),
      lng:     parseFloat(result.lon),
      label:   result.name || result.display_name.split(',')[0].trim(),
    })
  }

  return (
    <div className="atm-address-wrap">
      <input
        type="text"
        className="atm-address-input"
        placeholder="Hotel, Airbnb, venue address..."
        value={inputVal}
        onChange={handleInput}
        onBlur={() => setTimeout(() => setShowSug(false), 180)}
        onFocus={() => suggestions.length > 0 && setShowSug(true)}
        autoComplete="off"
      />

      {/* Spinning icon while the Nominatim request is in-flight */}
      {searching && (
        <span className="atm-address-spinner" aria-hidden="true">
          <i className="bi bi-arrow-repeat atm-spin" />
        </span>
      )}

      {/* Address suggestion dropdown */}
      {showSug && suggestions.length > 0 && (
        <ul className="atm-suggestions" role="listbox">
          {suggestions.map((r, i) => (
            <li
              key={r.place_id || i}
              className="atm-suggestion-item"
              role="option"
              aria-selected="false"
              onClick={() => pick(r)}
            >
              {/* Bold place name */}
              <div className="atm-suggestion-name">
                {r.name || r.display_name.split(',')[0]}
              </div>
              {/* Full address in muted text */}
              <div className="atm-suggestion-full">{r.display_name}</div>
            </li>
          ))}
        </ul>
      )}

      {/* Green confirmation tick once lat/lng are locked in */}
      {confirmed && (
        <div className="atm-confirmed">
          <i className="bi bi-check-circle-fill" aria-hidden="true" /> Location confirmed
        </div>
      )}
    </div>
  )
}


// ── BLANK FORM ────────────────────────────────────────────────
// Used when opening the modal for a new trip.
const emptyForm = {
  destination_label: '',
  stay_address:      '',
  stay_lat:          null,
  stay_lng:          null,
  arrive_dest_date:  '', // YYYY-MM-DD — stored as arrive_dest_at
  depart_dest_date:  '', // YYYY-MM-DD — stored as depart_dest_at
}


// ── ADD TRIP MODAL ────────────────────────────────────────────
// Props:
//   open         (boolean)   controls visibility
//   onClose      (function)  called when modal should close
//   onSave       (function)  called with the trip data object; async-safe
//   initialData  (object)    existing trip when editing; null for new trip
export default function AddTripModal({ open, onClose, onSave, initialData = null }) {
  const isEditing = !!initialData

  const [form,   setForm]   = useState(emptyForm)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})

  // Strip the time portion from an ISO datetime string, returning YYYY-MM-DD
  const toDate = (iso) => (iso ? iso.split('T')[0] : '')

  // ── Populate form when the modal opens ────────────────────
  useEffect(() => {
    if (!open) return
    if (initialData) {
      setForm({
        destination_label: initialData.destination_label || '',
        stay_address:      initialData.stay_address      || '',
        stay_lat:          initialData.stay_lat          ?? null,
        stay_lng:          initialData.stay_lng          ?? null,
        arrive_dest_date:  toDate(initialData.arrive_dest_at),
        depart_dest_date:  toDate(initialData.depart_dest_at),
      })
    } else {
      setForm(emptyForm)
    }
    setErrors({})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, open])

  // Single-field convenience setter
  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }))


  // ── VALIDATION ───────────────────────────────────────────
  // Stay address is optional — users may not know where they're
  // staying until closer to the trip. Name and dates are required.
  const validate = () => {
    const e = {}
    if (!form.destination_label.trim()) {
      e.destination_label = 'Give your trip a name.'
    }
    if (!form.arrive_dest_date) {
      e.arrive_dest_date = 'Enter your trip start date.'
    }
    if (!form.depart_dest_date) {
      e.depart_dest_date = 'Enter your trip end date.'
    }
    if (
      form.arrive_dest_date &&
      form.depart_dest_date &&
      form.depart_dest_date < form.arrive_dest_date
    ) {
      e.depart_dest_date = 'Trip end date must be on or after trip start date.'
    }
    setErrors(e)
    return Object.keys(e).length === 0
  }


  // ── SAVE ─────────────────────────────────────────────────
  const handleSave = async () => {
    if (!validate()) return

    // If the user typed an address without picking a suggestion,
    // attempt a geocode so we still get coordinates when possible.
    let { stay_lat, stay_lng } = form
    if (form.stay_address && (!stay_lat || !stay_lng)) {
      try {
        const candidates = await nominatimSearch(form.stay_address)
        if (candidates[0]) {
          stay_lat = parseFloat(candidates[0].lat)
          stay_lng = parseFloat(candidates[0].lon)
        }
      } catch {
        // Keep nulls on failure — coordinates are optional
      }
    }

    // Build the trip data object. Noon local time on the date
    // string keeps comparisons unambiguous across time zones.
    // Unused legacy fields are blanked for schema compatibility.
    const tripData = {
      trip_type:         'round_trip',
      destination_label: form.destination_label,
      stay_address:      form.stay_address,
      stay_lat,
      stay_lng,
      arrive_dest_at: form.arrive_dest_date ? `${form.arrive_dest_date}T12:00` : '',
      depart_dest_at: form.depart_dest_date ? `${form.depart_dest_date}T12:00` : '',
      // Travel-leg fields not collected — blanked for schema compatibility
      depart_home_at:          '',
      arrive_home_at:          '',
      transport_mode:          '',
      dest_timezone:           '',
      origin_terminal_address: '',
      origin_terminal_lat:     null,
      origin_terminal_lng:     null,
      origin_terminal_label:   '',
      dest_terminal_address:   '',
      dest_terminal_lat:       null,
      dest_terminal_lng:       null,
      dest_terminal_label:     '',
    }

    setSaving(true)
    try {
      await onSave(tripData)
      onClose()
    } catch (err) {
      setErrors({ global: err.message || 'Failed to save trip.' })
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  // Night-count badge shown in the hero once both dates are filled
  const nights = (() => {
    if (!form.arrive_dest_date || !form.depart_dest_date) return null
    const ms = new Date(form.depart_dest_date) - new Date(form.arrive_dest_date)
    const n  = Math.round(ms / 86400000)
    return n > 0 ? n : null
  })()


  // ── RENDER ───────────────────────────────────────────────
  return (
    <div
      className="atm-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      role="dialog"
      aria-modal="true"
      aria-label={isEditing ? 'Edit trip' : 'Add trip'}
    >
      <div className="atm-card">

        {/* ── HERO ──────────────────────────────────────── */}
        <div className="atm-hero">

          {/* Decorative background orb — purely visual */}
          <div className="atm-hero-orb" aria-hidden="true" />

          {/* Close button */}
          <button className="atm-close-btn" onClick={onClose} aria-label="Close">
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>

          {/* "New Trip" / "Edit Trip" pill */}
          <div className="atm-hero-pill">
            <i className="bi bi-suitcase-fill" aria-hidden="true" />
            {isEditing ? 'Edit Trip' : 'New Trip'}
          </div>

          {/* Dynamic heading — reflects the trip name as the user types */}
          <h2 className="atm-hero-heading">
            {form.destination_label || 'Where are you headed?'}
          </h2>

          {/* Night-count badge — appears once both dates are set */}
          {nights && (
            <div className="atm-night-badge">
              <i className="bi bi-moon-stars-fill" aria-hidden="true" />
              {nights} night{nights !== 1 ? 's' : ''}
            </div>
          )}

          {/* Global save error — shown inside the hero so it's hard to miss */}
          {errors.global && (
            <div className="atm-global-error" role="alert">
              <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" />
              {' '}{errors.global}
            </div>
          )}
        </div>
        {/* end hero */}


        {/* ── BODY ──────────────────────────────────────── */}
        <div className="atm-body">

          {/* Trip name */}
          <div className="form-group">
            <label className="atm-label" htmlFor="atm-trip-name">
              <i className="bi bi-tag-fill atm-label-icon" aria-hidden="true" />
              Trip name
            </label>
            <input
              id="atm-trip-name"
              type="text"
              placeholder="Chicago Weekend, Atlanta Gig"
              value={form.destination_label}
              onChange={e => set('destination_label', e.target.value)}
            />
            {errors.destination_label && (
              <span className="error-message" role="alert">{errors.destination_label}</span>
            )}
          </div>

          {/* Stay address — optional, can be added later once booked */}
          <div className="form-group">
            <label className="atm-label">
              <i className="bi bi-house-fill atm-label-icon" aria-hidden="true" />
              Where are you staying?
              <span className="atm-label-optional">optional</span>
            </label>
            <AddressField
              value={form.stay_address}
              onChange={v => set('stay_address', v)}
              onSelect={({ address, lat, lng, label }) =>
                setForm(prev => ({
                  ...prev,
                  stay_address: address,
                  stay_lat:     lat,
                  stay_lng:     lng,
                  // Auto-fill trip name from the location label if still blank
                  destination_label: prev.destination_label || label,
                }))
              }
              confirmed={!!(form.stay_lat && form.stay_lng)}
            />
          </div>

          {/* Date range row — both fields use the shared DateInput component */}
          <div className="atm-date-row">

            {/* Check-in / trip start */}
            <div className="atm-date-field">
              <DateInput
                id="atm-arrive-date"
                name="atm-arrive-date"
                label={
                  <span className="atm-label">
                    <i className="bi bi-box-arrow-right atm-label-icon" aria-hidden="true" />
                    Trip Start Date
                  </span>
                }
                value={form.arrive_dest_date}
                onChange={e => set('arrive_dest_date', e.target.value)}
                required
              />
              {errors.arrive_dest_date && (
                <span className="error-message" role="alert">{errors.arrive_dest_date}</span>
              )}
            </div>

            {/* Arrow divider — hidden on narrow screens via media query */}
            <div className="atm-date-arrow" aria-hidden="true">
              <i className="bi bi-arrow-right" />
            </div>

            {/* Check-out / heading home */}
            <div className="atm-date-field">
              <DateInput
                id="atm-depart-date"
                name="atm-depart-date"
                label={
                  <span className="atm-label">
                    <i className="bi bi-box-arrow-in-left atm-label-icon" aria-hidden="true" />
                    Trip End Date
                  </span>
                }
                value={form.depart_dest_date}
                onChange={e => set('depart_dest_date', e.target.value)}
                min={form.arrive_dest_date || undefined}
                required
              />
              {errors.depart_dest_date && (
                <span className="error-message" role="alert">{errors.depart_dest_date}</span>
              )}
            </div>
          </div>
          {/* end date range row */}

          {/* Informational note */}
          <div className="panel-pink-sm atm-info-note">
            <i className="bi bi-info-circle-fill atm-info-icon" aria-hidden="true" />
            <span>
              These dates shade your calendar to show when you're away from home.
            </span>
          </div>

          {/* Action buttons */}
          <div className="atm-actions">
            {/* btn base class required alongside btn-primary for correct sizing */}
            <button
              type="button"
              className="btn btn-primary atm-save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <i className="bi bi-arrow-repeat atm-spin atm-btn-icon" aria-hidden="true" />
                  Saving...
                </>
              ) : (
                <>
                  <i className="bi bi-check-lg atm-btn-icon" aria-hidden="true" />
                  {isEditing ? 'Update Trip' : 'Save Trip'}
                </>
              )}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>

        </div>
        {/* end body */}

      </div>
      {/* end card */}
    </div>
  )
}

